import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import moment from 'moment'
import { getTimeEntriesByProject, updateTimeEntry } from '../../entities/timeEntry/service'
import * as peopleService from '../../entities/people/service'
import { getAllTimesheets } from '../../entities/timesheet/service'
import {
  fetchAssignmentsInDateRange,
  fetchAssignmentsByProjectIdsInDateRange,
} from '../../entities/assignment/service'
import {
  getProjects,
  getExpensesByProjectIds,
  getRatesByProjectLead,
} from '../../entities/project/service'
import { approveExpenseBillingStatus } from '../../entities/expenses/service'
import { invalidate as invalidateTimeEntries } from '../../hooks/mutations/timeEntriesCache'
import { removeTypename } from '../../utils/removeTypename'

const initialState = {
  people: {},
  projects: [],
  timesheets: {
    submitted: [],
    open: [],
  },
  assignments: [],
  entries: [],
  availableWeeks: [],
  rates: {},
  expenses: [],
}

const approvalsReducer = (state, action) => {
  const resolvePayload = key =>
    typeof action.payload === 'function' ? action.payload(state[key]) : action.payload

  switch (action.type) {
    case 'SET_PEOPLE':
      return { ...state, people: resolvePayload('people') }
    case 'SET_PROJECTS':
      return { ...state, projects: resolvePayload('projects') }
    case 'SET_TIMESHEETS':
      return { ...state, timesheets: resolvePayload('timesheets') }
    case 'SET_ASSIGNMENTS':
      return { ...state, assignments: resolvePayload('assignments') }
    case 'SET_ENTRIES':
      return { ...state, entries: resolvePayload('entries') }
    case 'SET_AVAILABLE_WEEKS':
      return { ...state, availableWeeks: resolvePayload('availableWeeks') }
    case 'SET_RATES':
      return { ...state, rates: resolvePayload('rates') }
    case 'SET_EXPENSES':
      return { ...state, expenses: resolvePayload('expenses') }
    case 'CLEAR_PROJECT_DATA':
      return {
        ...state,
        people: {},
        timesheets: { submitted: [], open: [] },
        assignments: [],
        entries: [],
        availableWeeks: [],
        expenses: [],
      }
    default:
      return state
  }
}

const getMonthWeeks = (startDate, endDate, timesheets) => {
  const weeks = []
  const start = moment(startDate).startOf('month')
  const end = moment(endDate).endOf('month')
  const currentWeek = start.clone().startOf('week')
  while (currentWeek.isBefore(end)) {
    if (
      timesheets.some(ts => {
        return (
          moment(ts.startDate).isBetween(
            currentWeek,
            currentWeek.clone().endOf('week'),
            null,
            '[]',
          ) &&
          ts.entries.length > 0 &&
          ts.entries.some(entry => entry.approveState?.status === 'pending')
        )
      })
    ) {
      weeks.push({
        start: currentWeek.clone(),
        end: currentWeek.clone().endOf('week'),
        stringWeek: `Week of ${currentWeek.clone().format('MM/DD/YYYY')}`,
      })
    }

    currentWeek.add(1, 'week')
  }
  return weeks
}

const getOldestDate = dates => {
  return dates.reduce((acc, date) => {
    return date < acc ? date : acc
  }, new Date())
}

const getNewestDate = dates => {
  return dates.reduce((acc, date) => {
    return date > acc ? date : acc
  }, new Date(0))
}

const sortByName = projects =>
  [...projects].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
  )

const getLeadingProjects = async (isSuperUser, client) => {
  const allProjects = await getProjects(client)

  if (isSuperUser) {
    return sortByName(allProjects)
  }

  const userAssignments = await fetchAssignmentsInDateRange({
    client,
    startISO: new Date(0).toISOString(),
    endISO: new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString(),
  })

  const leadingAssignments = userAssignments.filter(assignment => assignment.type !== 0)
  return sortByName(
    allProjects.filter(project => {
      return leadingAssignments.some(assignment => assignment.projectId === project._id)
    }),
  )
}

const useApprovalsData = ({
  client,
  person,
  selectedDelegateId,
  isSuperUser,
  selectedProjectId,
  isLoadingFeatureFlags,
}) => {
  const [state, dispatch] = useReducer(approvalsReducer, initialState)
  const [isLoadingProjectData, setIsLoadingProjectData] = useState(false)
  const [isProjectsLoaded, setIsProjectsLoaded] = useState(false)
  const fetchAbortRef = useRef(0)

  // Phase 1: On mount — fetch rates and projects list
  useEffect(() => {
    if (!person._id) return
    getRatesByProjectLead(client, person._id, selectedDelegateId).then(result => {
      dispatch({
        type: 'SET_RATES',
        payload: result?.rates,
      })
    })
  }, [person._id])

  useEffect(() => {
    if (isLoadingFeatureFlags) return
    getLeadingProjects(isSuperUser, client)
      .then(leadingProjects => {
        dispatch({ type: 'SET_PROJECTS', payload: leadingProjects })
      })
      .catch(() => {
        dispatch({ type: 'SET_PROJECTS', payload: [] })
      })
      .finally(() => {
        setIsProjectsLoaded(true)
      })
  }, [isLoadingFeatureFlags])

  // Phase 2: On project selection — fetch project-specific data on-demand
  useEffect(() => {
    if (!selectedProjectId) return

    const fetchId = ++fetchAbortRef.current

    const fetchProjectData = async () => {
      setIsLoadingProjectData(true)
      dispatch({ type: 'CLEAR_PROJECT_DATA' })

      try {
        // Fetch assignments + people and entries + expenses in parallel
        const [
          assignmentsResult,
          pendingTimeEntries,
          approvedTimeEntries,
          expensesResult,
        ] = await Promise.all([
          fetchAssignmentsByProjectIdsInDateRange({
            projectIds: selectedProjectId,
            client,
            endISO: new Date().toISOString(),
            startISO: new Date(0).toISOString(),
          }),
          getTimeEntriesByProject({
            projectId: selectedProjectId,
            approveState: 'pending',
            client,
          }),
          getTimeEntriesByProject({
            projectId: selectedProjectId,
            approveState: 'approved',
            client,
            onlyInActiveInvoice: false,
          }),
          getExpensesByProjectIds(client, selectedProjectId),
        ])

        // Check if this fetch is still current
        if (fetchId !== fetchAbortRef.current) return

        // Process assignments and people
        const uniquePeopleIdsFromAssignments = new Set(
          assignmentsResult.map(assignment => assignment.personId),
        )

        const entries = [...pendingTimeEntries, ...approvedTimeEntries]
        const uniquePeopleIdsFromEntries = new Set(entries.map(entry => entry.personId))

        const allUniquePeopleIds = new Set([
          ...uniquePeopleIdsFromAssignments,
          ...uniquePeopleIdsFromEntries,
        ])

        const peopleData = await peopleService.getAllPeople(client, {
          peopleIds: [...allUniquePeopleIds],
        })

        if (fetchId !== fetchAbortRef.current) return

        const peopleHash = peopleData.reduce((acc, personData) => {
          acc[personData._id] = personData
          return acc
        }, {})

        dispatch({ type: 'SET_PEOPLE', payload: peopleHash })
        dispatch({ type: 'SET_ASSIGNMENTS', payload: assignmentsResult || [] })
        dispatch({ type: 'SET_ENTRIES', payload: entries })

        // Process expenses
        const regex = /^(53002|53003|53004|53005|53006|53007|53008|53009|53011|53012|54000|54001|54002|54003|54004|54005|54006|54007|54008|54009|54012)/
        const filteredExpenses = (expensesResult?.expenses || []).filter(expense => {
          return regex.test(String(expense.account)) && !expense.stacksQuickbooksInvoiceId
        })
        dispatch({ type: 'SET_EXPENSES', payload: filteredExpenses })

        // Fetch timesheets after people and entries are available
        if (Object.keys(peopleHash).length > 0 && entries.length > 0) {
          const moment2MonthsAgo = moment().subtract(2, 'months')
          const endDate = moment()
            .endOf('month')
            .toISOString()

          const timesheets = await getAllTimesheets({
            variables: {
              personIds: Object.keys(peopleHash),
              startDate: moment2MonthsAgo.startOf('month').toISOString(),
              endDate,
            },
            client,
          })

          if (fetchId !== fetchAbortRef.current) return

          const submittedTimesheets = timesheets.filter(ts => ts.approvalStatus === 'Approved')
          const openTimesheets = timesheets.filter(ts => ts.approvalStatus !== 'Approved')

          const timesheetsWithPendingEntries = submittedTimesheets.map(timesheet => {
            const timesheetEntries = entries.filter(
              entry => entry.repliconTimesheetId === timesheet.repliconId,
            )
            return { ...timesheet, entries: timesheetEntries }
          })

          const oldestTimeEntryDate = getOldestDate(
            submittedTimesheets.map(ts => moment.utc(ts.startDate)),
          )
          const newestTimeEntryDate = getNewestDate(
            submittedTimesheets.map(ts => moment.utc(ts.startDate)),
          )

          const availableWeeks = getMonthWeeks(
            oldestTimeEntryDate,
            newestTimeEntryDate,
            timesheetsWithPendingEntries,
          )

          dispatch({ type: 'SET_AVAILABLE_WEEKS', payload: availableWeeks })
          dispatch({
            type: 'SET_TIMESHEETS',
            payload: { submitted: timesheetsWithPendingEntries, open: openTimesheets },
          })
        }
      } finally {
        if (fetchId === fetchAbortRef.current) {
          setIsLoadingProjectData(false)
        }
      }
    }

    fetchProjectData()
  }, [selectedProjectId])

  const onEntryUpdate = useCallback(
    entry => {
      const projectEntries = state.entries[entry.projectId]

      const updatedEntries = projectEntries.map(projectData => {
        if (entry.personId !== projectData.personId) return projectData
        const updatedTimeEntries = projectData.timeEntries.filter(projectEntry => {
          return projectEntry._id !== entry._id
        })

        return { ...projectData, timeEntries: updatedTimeEntries }
      })

      dispatch({
        type: 'SET_ENTRIES',
        payload: {
          ...state.entries,
          [entry.projectId]: updatedEntries,
        },
      })
    },
    [state.entries],
  )

  const updateEntryBack = useCallback(
    async entry => {
      const result = await updateTimeEntry({
        client,
        timeEntry: entry,
        selectedDelegateId,
      })
      return result
    },
    [client, onEntryUpdate],
  )

  const onUpdateManyEntries = useCallback(
    async entries => {
      let results
      try {
        results = await Promise.all(
          entries.map(async updatedEntry => {
            const cleanEntry = removeTypename(updatedEntry)
            return updateEntryBack(cleanEntry)
          }),
        )
      } finally {
        await invalidateTimeEntries()
      }

      dispatch({
        type: 'SET_ENTRIES',
        payload: prevEntries => {
          const entryMap = new Map(prevEntries.map(e => [e._id, e]))

          results.forEach(result => {
            entryMap.set(result._id, result)
          })

          return Array.from(entryMap.values())
        },
      })
    },
    [updateEntryBack],
  )

  const onReject = useCallback(
    entry => {
      const updatedEntries = state.entries.filter(personEntry => personEntry._id !== entry._id)

      dispatch({
        type: 'SET_ENTRIES',
        payload: updatedEntries,
      })
    },
    [state.entries],
  )

  const onSplitEntry = useCallback(
    ({ parent, child }) => {
      const copyEntries = [...state.entries]
      const updateOrAdd = entry => {
        const idx = copyEntries.findIndex(e => e._id === entry._id)
        if (idx === -1) {
          copyEntries.push({ ...entry })
        } else {
          copyEntries[idx] = { ...entry }
        }
      }
      updateOrAdd(parent)
      updateOrAdd(child)
      dispatch({
        type: 'SET_ENTRIES',
        payload: copyEntries,
      })
    },
    [state.entries],
  )

  const onApproveExpenseBillingStatus = useCallback(
    async (expenseQuickbooksId, billingStatus, personId) => {
      const approveState = {
        status: 'approved',
        modifiedBy: personId,
        modifiedOn: moment
          .utc()
          .toDate()
          .toISOString(),
      }

      await approveExpenseBillingStatus(client, expenseQuickbooksId, billingStatus, approveState)

      const updatedExpenses = [...state.expenses]
      const expenseIndex = state.expenses.findIndex(
        expense => expense.quickbooksId === expenseQuickbooksId,
      )
      const expense = { ...state.expenses[expenseIndex], billingStatus, approveState }
      updatedExpenses[expenseIndex] = expense

      dispatch({
        type: 'SET_EXPENSES',
        payload: updatedExpenses,
      })
    },
    [state.expenses],
  )

  const onUpdateExpense = useCallback((expenseId, expenseData) => {
    dispatch({
      type: 'SET_EXPENSES',
      payload: prevExpenses =>
        prevExpenses.map(exp => (exp._id === expenseId ? { ...exp, ...expenseData } : exp)),
    })
  }, [])

  const onSplitExpense = useCallback(
    ({ parent, child }) => {
      const updatedExpenses = [...state.expenses]
      const updateOrAdd = expense => {
        const idx = updatedExpenses.findIndex(e => e._id === expense._id)
        if (idx === -1) {
          updatedExpenses.push({ ...expense })
        } else {
          updatedExpenses[idx] = { ...expense }
        }
      }
      updateOrAdd(parent)
      updateOrAdd(child)

      dispatch({
        type: 'SET_EXPENSES',
        payload: updatedExpenses,
      })
    },
    [state.expenses],
  )

  return {
    state,
    isLoadingProjectData,
    isProjectsLoaded,
    actions: {
      onEntryUpdate,
      onUpdateManyEntries,
      onReject,
      onSplitEntry,
      onApproveExpenseBillingStatus,
      onUpdateExpense,
      onSplitExpense,
    },
  }
}

export default useApprovalsData
